/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from "react";
import { styled, borderRadius, fontSizes, inputHeight, muiInputFormsCSS, skeletonShimmer, StyledIconButton } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Popover } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useDragAndDropSortable } from "@iventis/drag-and-drop";
import { Content } from "@iventis/translations/content/typed-content";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useSearch } from "@iventis/search/hooks/use-search";
import { MapSearchResultType, SearchState } from "@iventis/search/types/types";
import { SearchResultGroupInterface } from "@iventis/search/interface/search-result-interface";
import debounce from "lodash.debounce";
import { SearchBarComponent } from "@iventis/search/components/search-bar";
import { SearchResults } from "@iventis/search/components/search-results";
import { RoutePlannerWaypoint } from "./route-planner";

const toAlpha = (num: number) => {
    if (num < 1 || num > 26 || typeof num !== "number") {
        return "Not a valid number";
    }
    const leveller = 64;
    // since actually A is represented by 65 and we want to represent it with one
    return String.fromCharCode(num + leveller);
};

const WayPointOrder: React.FC<{ order: number; active: boolean }> = ({ order, active }) => <StyledWayPointOrder $active={active}>{toAlpha(order)}</StyledWayPointOrder>;

const WaypointFinder: React.FC<{
    waypoint: RoutePlannerWaypoint;
    searchResultGroups: SearchResultGroupInterface<MapSearchResultType>[];
    deleteWaypoint: (id: string) => void;
    allowDelete: boolean;
    onFocus: () => void;
    onClear: () => void;
    autoFocus?: RoutePlannerWaypoint;
    order: number;
}> = ({ waypoint, searchResultGroups, deleteWaypoint, allowDelete, autoFocus, onFocus, onClear, order }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);
    const searchBarComponentRef = React.useRef<HTMLInputElement>(null);
    const { setRefs, attributes, listeners, transitionStyle } = useDragAndDropSortable(waypoint.id, true);
    const [searchValue, setSearchValue] = useState(waypoint.name);
    const [handleSearch, searchResults, state, searchedString] = useSearch(searchResultGroups);

    const updateSearch = React.useRef(debounce(handleSearch, 400));
    const translate = useIventisTranslate();

    const handleSearchValueChange = (value: string) => {
        setSearchValue(value);
        setAnchorEl(searchBarComponentRef.current);
        // get api search results here
        updateSearch.current(value);
        if (value === "") {
            onClear();
        }
    };
    const popoverOpen = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isAutoFocus = waypoint.id === autoFocus?.id;

    return (
        <StyledWrapper key={waypoint.id} ref={setRefs} style={transitionStyle} className="waypoint-finder" data-testid={`waypoint-draggable-wrapper-${waypoint.name}`}>
            <StyledDraggableArea {...attributes} {...listeners} data-testid={`waypoint-draggable-${waypoint.name}`} />
            <WayPointOrder order={order} active={searchValue?.length > 0} />
            <StyledSearchWrapper ref={searchBarComponentRef}>
                <SearchBarComponent
                    key={`${waypoint.id}-${isAutoFocus}`}
                    placeHolderText={translate(Content.map7.routeGenerator.search)}
                    value={searchValue}
                    onValueChange={handleSearchValueChange}
                    fontSize="small"
                    debounceMs={400}
                    className="waypoint-search-bar"
                    testid={`waypoint-search-bar-${waypoint.order}`}
                    loading={state === SearchState.SEARCHING}
                    onFocus={onFocus}
                    autoFocus={isAutoFocus}
                    sx={{ zIndex: 200 }}
                />
                {state !== SearchState.IDLE && (
                    <StyledPopover
                        disableAutoFocus
                        disableEnforceFocus
                        id={`search-results-${waypoint.id}`}
                        open={popoverOpen}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                    >
                        <SearchResults
                            searching={state === SearchState.SEARCHING}
                            resultGroups={searchResults}
                            noResultsMessage={translate(Content.map.search.no_results)}
                            searchString={searchedString}
                        />
                    </StyledPopover>
                )}
            </StyledSearchWrapper>
            {/* -10px margin because in this case we don't want a gap */}
            <StyledIconButton sx={{ fontSize: fontSizes.small, zIndex: 200, marginLeft: "-10px" }} disabled={allowDelete} onClick={() => deleteWaypoint(waypoint.id)}>
                <FontAwesomeIcon icon={["far", "trash"]} />
                <span className="hidden">{translate(Content.map7.routeGenerator.removeStop)}</span>
            </StyledIconButton>
        </StyledWrapper>
    );
};

const StyledWrapper = styled.div`
    ${muiInputFormsCSS}
    display: flex;
    gap: 12px;
    place-items: center;
    position: relative;

    .waypoint-search-bar {
        width: 100%;
    }
`;

export const SkeletonWaypoint = styled.span`
    ${skeletonShimmer({ sequence: 0 })};
    transform: scale(0.9);
    overflow: hidden;
    height: ${inputHeight};
    width: 100%;
    border-radius: ${borderRadius.input};
`;

const StyledWayPointOrder = styled.div<{ $active: boolean }>`
    box-sizing: border-box;
    ${(props: { theme: Theme; $active: boolean }) =>
        props.$active
            ? `background: ${props.theme.secondaryColors.dark}; border: 1px solid transparent`
            : `background: ${props.theme.secondaryColors.blank}; border: 1px solid ${props.theme.secondaryColors.dark}`};
    padding: 10px;
    border-radius: 100%;
    width: 33px;
    height: 33px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

const StyledSearchWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledPopover = styled(Popover)`
    .MuiPaper-root {
        box-sizing: border-box;
        width: 313px;
        padding: 12px;
    }
`;

const StyledDraggableArea = styled.div`
    cursor: grab;
    z-index: 100;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
`;

export default WaypointFinder;
